
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.extend(utc);
dayjs.locale('en-gb');

// Consistent date formatting functions
const formatDisplayDate = (date: dayjs.Dayjs | null) => {
  if (!date) return '';
  return date.format('DD/MM/YYYY');
};

const formatDisplayDateTime = (date: any) => {
  if (!date) return '';
  // Handle string dates that might have milliseconds
  if (typeof date === 'string' && date.includes('.')) {
    date = date.split('.')[0];
  }
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format('DD/MM/YYYY HH:mm:ss') : '';
};

const formatApiDateTime = (date: dayjs.Dayjs | null) => {
  if (!date) return '';
  return date.format('YYYY-MM-DD HH:mm:ss');
};

const formatApiDate = (date: dayjs.Dayjs | null) => {
  if (!date) return '';
  return date.format('YYYY-MM-DD');
};

// Define columns for the report download service
const columns = [
  {
    field: 'id',
    headerName: 'ID',
    flex: 0.5
  },
  {
    field: 'screenPath',
    headerName: 'Screen Path',
    flex: 2
  },
  {
    field: 'action',
    headerName: 'Action',
    flex: 1
  },
  {
    field: 'createdByName',
    headerName: 'Action By',
    flex: 1
  },
  {
    field: 'createdDateStr',
    headerName: 'Action Date',
    flex: 1.5
  }
];

const RedirectionsLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState<Dayjs | null>(dayjs());
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  useEffect(() => {
  const fetchData = async () => {
    if (!filterDate) return;

    setLoading(true);

    try {
      const response = await get(
        `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/redirection_logs?filterDate=${formatApiDate(filterDate)}`
      );

      const dataWithId = (response || []).map((item: any, index: number) => ({
        id: index + 1,
        ...item,
        createdDateStr: formatDisplayDateTime(item.createdDate)
      }));

      setRows(dataWithId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [filterDate, vendorId]);

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    return matchesSearch;
  });

 

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!filterDate) {
      alert('Please select a date');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const reportDate = formatDisplayDate(filterDate);
      const dateStr = formatApiDate(filterDate);
      
      // Generate custom filename
      const customFileName = `Package_Channel_Modification_Report_${dateStr}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Get action distribution statistics
      const actionTypes: Record<string, number> = {};
      filteredRows.forEach(row => {
        const action = row.action || 'Unknown';
        actionTypes[action] = (actionTypes[action] || 0) + 1;
      });

      const success = await reportDownloadService.downloadReport({
        title: 'Package Channel Modification History Report',
        columns,
        rows: filteredRows,
        month: filterDate.month() + 1,
        year: filterDate.year(),
        fileName: customFileName,
        
        // Excel specific headers
        excelHeaders: [
  "ID",
  "Screen Path",
  "Action",
  "Action By",
  "Action Date"
],
        excelRowMapper: (row: any) => [
  row.id || '',
  row.screenPath || '',
  row.action || '',
  row.createdByName || '',
  row.createdDateStr || ''
]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Package Channel Modification Report</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Redirected Logs"
              subHeading=""
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload('excel')}
                  disabled={downloading.excel}
                  sx={{ minWidth: '160px' }}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading.pdf}
                  sx={{ minWidth: '160px' }}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* Filter Section */}
      <Box sx={{ px: 2, pt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="Search by package, channel, or action..."
            />
            <DatePicker
              label="Date (DD/MM/YYYY)"
              value={filterDate}
              onChange={(newValue) => {
                if (newValue && dayjs(newValue).isValid()) {
                  setFilterDate(newValue);
                }
              }}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small',
                  sx: { flex: 1, minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
           
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* Table Section */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              paddingTop: '12px',
              paddingBottom: '12px',
            },
            '& .MuiDataGrid-cell:hover': {
              color: 'rgb(43 44 45)',
            },
          }}
        />
      </Box>
    </>
  );
};

export default RedirectionsLogs;